/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.03.09 at 13:34
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { forEach, getAttr, getGuid, isArray, isFunction, isPlainObject, isString, isUndefined, map } from '@labor-digital/helferlein';
import { runInAction } from 'mobx';
import { getElementValue, setElementAttribute, setElementValue } from './Binding/util';
import { bitEventActionWrap, findClosest, findElement, resolveEventTarget } from './util';
var AbstractBit = /** @class */ (function () {
    function AbstractBit(context) {
        this._context = context;
        context.di.pluginLoader.extendBit(this);
    }
    Object.defineProperty(AbstractBit, "__bit", {
        /**
         * Marker to detect if this element is a bit class
         * @internal
         */
        get: function () {
            return true;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractBit.prototype, "$el", {
        /**
         * Returns the instance of the bit mount / root dom node for this bit
         * @protected
         */
        get: function () {
            return this._context.mount.el;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractBit.prototype, "$context", {
        /**
         * Returns the context of this bit containing connected instances and meta information
         * @protected
         */
        get: function () {
            return this._context;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractBit.prototype, "$di", {
        /**
         * Returns the di container instance of the application
         * @protected
         */
        get: function () {
            return this._context.di;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractBit.prototype, "$app", {
        /**
         * Returns the root app this bit was linked with
         * @protected
         */
        get: function () {
            return this.$di.app;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractBit.prototype, "$eventBus", {
        /**
         * Returns the instance of the global event bus that allows cross-bit event emitting
         * @protected
         */
        get: function () {
            return this.$di.eventBus;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(AbstractBit.prototype, "$proxy", {
        /**
         * Returns the component proxy instance to keep track of event listeners
         * and other outside connections.
         *
         * @see ComponentProxy to find out what it does
         * @protected
         */
        get: function () {
            return this._context.proxy;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Allows you to find the "closest" parent element inside the dom elements of this bit mount
     *
     * @param selector any query selector to find your element with. As a "magic" helper you can provide "@my-ref"
     * that will be converted into '*[data-ref="my-ref"]' internally before the query is resolved.
     * @param to The pivot element from which the closest element should be resolved
     * @param includeParents By default only elements inside the given mount are resolved.
     * If you set this to true, results outside the bounds of the mount will be returned as well.
     * @protected
     */
    AbstractBit.prototype.$closest = function (selector, to, includeParents) {
        this.$context.reactivityProvider.domChangeDependency();
        return findClosest(this.$el, selector, to, includeParents);
    };
    AbstractBit.prototype.$find = function (selector, deepOrPivot, deep) {
        var _a;
        this.$context.reactivityProvider.domChangeDependency();
        return (_a = findElement(this.$el, selector, false, deepOrPivot, deep)[0]) !== null && _a !== void 0 ? _a : null;
    };
    AbstractBit.prototype.$findAll = function (selector, deepOrPivot, deep) {
        this.$context.reactivityProvider.domChangeDependency();
        return findElement(this.$el, selector, true, deepOrPivot, deep);
    };
    AbstractBit.prototype.$on = function (a, b, c) {
        return bitEventActionWrap.call(this, 'bind', a, b, c);
    };
    AbstractBit.prototype.$off = function (a, b, c) {
        return bitEventActionWrap.call(this, 'unbind', a, b, c);
    };
    AbstractBit.prototype.$emit = function (a, b, c) {
        var _this = this;
        var hasTarget = isString(b);
        var event = hasTarget ? b : a;
        var args = hasTarget ? c : b;
        forEach(resolveEventTarget.call(this, hasTarget ? a : undefined), function (target) {
            _this.$proxy.emit(target, event, args);
        });
        return this;
    };
    /**
     * Registers a callback which is executed every time the property was updated
     * @see https://mobx.js.org/reactions.html#reaction
     *
     * @param target  Either the name of the property to watch, or an expression to define the watchable target,
     *                like () => this.computed
     * @param watcher The callback to execute when the property was updated.
     *                It will receive the new and the old value as parameters.
     */
    AbstractBit.prototype.$watch = function (target, watcher) {
        return this._context.reactivityProvider.addWatcher(target, watcher);
    };
    /**
     * Registers a new auto-runner for this bit, and automatically adds it to the garbage collection
     * @see https://mobx.js.org/reactions.html#autorun
     *
     * @param watcher The function to execute, when one or more of the used observables changed
     * @param options Additional options
     */
    AbstractBit.prototype.$autoRun = function (watcher, options) {
        return this._context.reactivityProvider.addAutoRun(watcher, options);
    };
    /**
     * Shortcut to mobx "runInAction" helper
     * @see https://mobx.js.org/actions.html#runinaction
     *
     * @param fn The function to execute in an action
     * @protected
     */
    AbstractBit.prototype.$runInAction = function (fn) {
        return runInAction(fn);
    };
    /**
     * Helper to tell this and all parent mounts that the DOM was somehow changed.
     * This forces all elements in the tree to recalculate their bindings and reattach the event listeners.
     *
     * @protected
     */
    AbstractBit.prototype.$domChanged = function () {
        this.$emit('domChange');
    };
    /**
     * Sets / removes a given attribute from the list of given elements
     * @param element the element / elements to set or remove the attribute for
     * @param a
     * @param b
     */
    AbstractBit.prototype.$attr = function (element, a, b) {
        if (!element) {
            return this;
        }
        if (isString(element)) {
            element = this.$findAll(element);
        }
        if (isUndefined(b) && isString(a)) {
            if (isArray(element)) {
                return map(element, function (el) { return getAttr(el, a); });
            }
            // @todo in the next major version, this should not return an array
            return [getAttr(element, a)];
        }
        var setter = function (element) {
            if (isPlainObject(a)) {
                forEach(a, function (v, k) { return setElementAttribute(element, k, v); });
            }
            else {
                setElementAttribute(element, a, b !== null && b !== void 0 ? b : null);
            }
        };
        if (element instanceof HTMLElement || element instanceof Element) {
            setter(element);
        }
        else {
            forEach(element, function (el) { return setter(el); });
        }
        return this;
    };
    /**
     * Sets / removes a the given css style properties from the list of given elements
     * @param element the element / elements to set or remove the css properties for
     * @param styles An object of css styles, with properties in the same naming schema
     * like you would use in node using element.style.[fontFamily,display,...],
     * or as a string (like in a html "style" attribute), or null to completely remove the style attribute.
     * @protected
     */
    AbstractBit.prototype.$style = function (element, styles) {
        return this.$attr(element, 'style', styles);
    };
    /**
     * Sets / removes css classes on the list of given elements
     * @param element the element / elements to set or remove the css classes to/from
     * @param classes An object of css classes where each property in the classInfo argument and adds the property name to the
     * element's class if the property value is truthy; if the property value is falsey, the property name is removed
     * from the element's class. For example {foo: bar} applies the class foo if the value of bar is truthy.
     * or as a string (like in a html "class" attribute), or null to completely remove the class attribute.
     * @protected
     */
    AbstractBit.prototype.$class = function (element, classes) {
        return this.$attr(element, 'class', classes);
    };
    /**
     * Utility to load the content of a "template" tag into a new sub-node which will be returned.
     * To be selectable, your template MUST have a data-ref="$ref" attribute.
     *
     * The method allows you to provide a one-dimensional list of values that should be injected
     * while the template is loaded. NOTE: This is not reactive, but merely an initial state.
     * To define a marker add the data-value="key" attribute to any child of your template tag,
     * and provide the data like {key: 'my-value'}. All data is injected as text and therefore auto-escaped.
     *
     * NOTE: If your template contains binding attributes like data-bind or data-model, you have to execute
     * the $domChanged() method once, after the node was attached to the dom tree!
     *
     * ADVICE: For advanced templating tasks I would strongly advise you, to use $html() instead, as it is reactive
     * to any data changes and allows special features like event-listeners.
     *
     * @param ref
     * @param targetOrData
     * @param dataOrAdapter
     * @param adapter
     * @protected
     */
    AbstractBit.prototype.$tpl = function (ref, targetOrData, dataOrAdapter, adapter) {
        var _this = this;
        var _a, _b;
        var _tpl = this.$find('template[data-ref="' + ref + '"]');
        if (!_tpl) {
            throw new Error('Could not find a template with the data-ref="' + ref + '" attribute on it!');
        }
        var hash = _tpl._bitTplHash = (_a = _tpl._bitTplHash) !== null && _a !== void 0 ? _a : getGuid();
        var tpl = _tpl.innerHTML + '';
        var target;
        if (targetOrData) {
            if (isString(targetOrData)) {
                target = (_b = this.$find(targetOrData)) !== null && _b !== void 0 ? _b : undefined;
            }
            else if (!isPlainObject(targetOrData)) {
                target = targetOrData;
            }
        }
        var renderer = this.$di.templateRenderer;
        if (target) {
            if (!isFunction(dataOrAdapter)) {
                if (isPlainObject(dataOrAdapter)) {
                    console.error('Your data has to be wrapped into a function when you want to use dynamic re-rendering!');
                }
                dataOrAdapter = function () { return ({}); };
            }
            return this.$context.reactivityProvider.addAutoRun(function () {
                target.innerHTML = renderer.render(tpl, dataOrAdapter(), hash, adapter);
                setTimeout(function () { _this.$domChanged(); }, 1);
            });
        }
        else {
            var _tmp = document.createElement('template');
            _tmp.innerHTML = renderer.render(tpl, isPlainObject(targetOrData) ? targetOrData : {}, hash, dataOrAdapter);
            return _tmp.content;
        }
    };
    AbstractBit.prototype.$val = function (element, value) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (isString(element)) {
                    element = this.$find(element);
                }
                if (!element) {
                    return [2 /*return*/];
                }
                if (isUndefined(value)) {
                    return [2 /*return*/, getElementValue(element)];
                }
                else {
                    setElementValue(element, value);
                }
                return [2 /*return*/];
            });
        });
    };
    /**
     * This is the DANGER ZONE! Calling this method will destroy the bit instance completely
     * @protected
     */
    AbstractBit.prototype.$destroy = function () {
        if (this.beforeDestroy) {
            this.beforeDestroy();
        }
        var pluginLoader = this._context.di.pluginLoader;
        this._context.destroy();
        this._context = null;
        pluginLoader.destroyBit(this);
    };
    return AbstractBit;
}());
export { AbstractBit };
//# sourceMappingURL=AbstractBit.js.map